import React from "react";
import {
  SlidersHorizontal, Building2, CalendarOff, GitBranch, UserPlus, ListChecks,
  Sparkles, Plus, ArrowRight,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { PageShell } from "../../components/PageShell";
import { SectionCard, Button, Badge } from "../../components/primitives";
import { DataTable, EmptyState } from "../../components/DataTable";
import { Input, Field, Select } from "../../components/forms";
import { Stack, Row, Toggle, Hint, StatusBadge } from "./kit";
import { useHrms, useFetch } from "./useHrms";

// HRMS ▸ HR Settings — the config that drives every module. Shifts live in
// Attendance, pay components in Payroll, holidays in Leave; this page owns
// what has no better home: bootstrap, org units, leave types, approval rules,
// onboarding templates and profile fields.

const TABS = [
  { key: "org", label: "Organization", icon: <Building2 size={14} /> },
  { key: "leave", label: "Leave types", icon: <CalendarOff size={14} /> },
  { key: "approvals", label: "Approvals", icon: <GitBranch size={14} /> },
  { key: "onboarding", label: "Onboarding", icon: <UserPlus size={14} /> },
  { key: "fields", label: "Profile fields", icon: <ListChecks size={14} /> },
];

export default function HrmsSettings() {
  const t = useDashTheme();
  const [tab, setTab] = React.useState("org");

  return (
    <PageShell
      eyebrow="HRMS"
      title="HR Settings"
      subtitle="Configuration that drives every HRMS module"
      icon={<SlidersHorizontal size={16} />}
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === "org" && <OrgTab t={t} />}
      {tab === "leave" && <LeaveTab t={t} />}
      {tab === "approvals" && <ApprovalsTab t={t} />}
      {tab === "onboarding" && <OnboardingTab t={t} />}
      {tab === "fields" && <FieldsTab t={t} />}
    </PageShell>
  );
}

// ── Organization ──
function OrgTab({ t }) {
  const { isHr, post, del } = useHrms();
  const { data, loading, refresh } = useFetch("/org-units");
  const [form, setForm] = React.useState({ type: "department", name: "" });
  const [initResult, setInitResult] = React.useState(null);
  const [initBusy, setInitBusy] = React.useState(false);

  const initialize = async () => {
    setInitBusy(true);
    try {
      const res = await post("/settings/initialize", {});
      const n = Object.values(res.created || {}).reduce((s, x) => s + x, 0);
      setInitResult(n > 0 ? `Seeded ${n} default records.` : "Already initialized — nothing to add.");
    } finally { setInitBusy(false); }
  };

  const add = async () => {
    if (!form.name.trim()) return;
    await post("/org-units", form);
    setForm({ ...form, name: "" });
    refresh();
  };

  const rows = data || [];
  const groups = ["department", "location", "business_unit", "cost_center"];

  return (
    <Stack gap={t.s[4]}>
      {isHr && (
        <SectionCard title="Bootstrap" subtitle="One click seeds leave types, shifts, pay components, approval rules, an onboarding template and profile fields. Idempotent — safe to re-run." padding={16}
          actions={<Button variant="primary" leftIcon={<Sparkles size={13} />} onClick={initialize} disabled={initBusy}>{initBusy ? "Seeding…" : "Initialize HRMS defaults"}</Button>}>
          {initResult ? <Badge tone="positive">{initResult}</Badge> : <Hint>Run this once per organization before using leave, payroll or onboarding.</Hint>}
        </SectionCard>
      )}

      <SectionCard title="Org units" subtitle="Departments, locations, business units and cost centers" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "name", header: "Name" },
            { key: "type", header: "Type", render: (r) => <Badge tone="neutral">{cap(r.type)}</Badge> },
            {
              key: "act", header: "", align: "right",
              render: (r) => isHr ? <Button size="sm" variant="ghost" onClick={async () => { await del(`/org-units/${r._id}`); refresh(); }}>Remove</Button> : null,
            },
          ]}
          rows={rows}
          emptyState={<EmptyState title="No org units" description="Add departments and locations below." />}
        />
      </SectionCard>

      {isHr && (
        <SectionCard title="Add org unit" padding={16}>
          <Row gap={12} wrap align="flex-end">
            <Field label="Type">
              <Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                {groups.map((g) => <option key={g} value={g}>{cap(g)}</option>)}
              </Select>
            </Field>
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Engineering" /></Field>
            <Button variant="primary" leftIcon={<Plus size={13} />} onClick={add}>Add</Button>
          </Row>
        </SectionCard>
      )}

      <Hint>Shifts are managed in Attendance ▸ Shifts & roster, pay components in Payroll ▸ Components, and holidays in Leave ▸ Holidays.</Hint>
    </Stack>
  );
}

// ── Leave types ──
function LeaveTab({ t }) {
  const { isHr, post, patch } = useHrms();
  const { data, loading, refresh } = useFetch("/leave/types");
  const [form, setForm] = React.useState({ name: "", annualQuota: "", accrualFrequency: "monthly", accrualAmount: "", encashable: false });

  const add = async () => {
    if (!form.name.trim()) return;
    await post("/leave/types", {
      ...form,
      annualQuota: Number(form.annualQuota) || 0,
      accrualAmount: Number(form.accrualAmount) || 0,
    });
    setForm({ name: "", annualQuota: "", accrualFrequency: "monthly", accrualAmount: "", encashable: false });
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard title="Leave types" subtitle="Quota, accrual and encashability per type" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "name", header: "Type" },
            { key: "annualQuota", header: "Quota / yr", align: "right" },
            { key: "accrualFrequency", header: "Accrual", render: (r) => cap(r.accrualFrequency) },
            { key: "carryForwardMax", header: "Carry forward", align: "right", render: (r) => r.carryForwardMax || "—" },
            { key: "encashable", header: "Encashable", render: (r) => isHr
              ? <Toggle checked={!!r.encashable} onChange={async (v) => { await patch(`/leave/types/${r._id}`, { encashable: v }); refresh(); }} />
              : (r.encashable ? <Badge tone="positive">Yes</Badge> : "—") },
            { key: "proofRequired", header: "Proof", render: (r) => r.proofRequired ? <Badge tone="warning">{r.proofRequiredAboveDays ? `> ${r.proofRequiredAboveDays}d` : "Required"}</Badge> : "—" },
            {
              key: "act", header: "", align: "right",
              render: (r) => isHr ? <Button size="sm" variant="ghost" onClick={async () => { await patch(`/leave/types/${r._id}`, { active: false }); refresh(); }}>Retire</Button> : null,
            },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No leave types" description="Initialize defaults on the Organization tab or add one below." />}
        />
      </SectionCard>

      {isHr && (
        <SectionCard title="Add leave type" padding={16}>
          <Row gap={12} wrap align="flex-end">
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Paternity Leave" /></Field>
            <Field label="Quota / yr"><Input type="number" value={form.annualQuota} onChange={(e) => setForm((f) => ({ ...f, annualQuota: e.target.value }))} /></Field>
            <Field label="Accrual">
              <Select value={form.accrualFrequency} onChange={(e) => setForm((f) => ({ ...f, accrualFrequency: e.target.value }))}>
                {["monthly", "half_yearly", "on_event", "on_approval", "none"].map((a) => <option key={a} value={a}>{cap(a)}</option>)}
              </Select>
            </Field>
            <Field label="Accrual amount"><Input type="number" step="0.01" value={form.accrualAmount} onChange={(e) => setForm((f) => ({ ...f, accrualAmount: e.target.value }))} /></Field>
            <Button variant="primary" leftIcon={<Plus size={13} />} onClick={add}>Add</Button>
          </Row>
        </SectionCard>
      )}
    </Stack>
  );
}

// ── Approvals ──
const CHAIN_ROLES = ["reporting_manager", "hr"];
const TRIGGERS = ["leave", "attendance_correction", "profile_change", "reimbursement", "resignation"];

function ApprovalsTab({ t }) {
  const { isHr, post, patch } = useHrms();
  const { data, loading, refresh } = useFetch("/approval-rules");
  const [form, setForm] = React.useState({ triggerType: "leave", label: "", chain: "reporting_manager", condField: "", condValue: "" });

  const add = async () => {
    if (!form.label.trim()) return;
    const body = {
      triggerType: form.triggerType,
      label: form.label,
      chain: form.chain === "both" ? ["reporting_manager", "hr"] : [form.chain],
    };
    if (form.condField && form.condValue) {
      body.condition = { field: form.condField, op: "gt", value: Number(form.condValue) };
    }
    await post("/approval-rules", body);
    setForm({ triggerType: "leave", label: "", chain: "reporting_manager", condField: "", condValue: "" });
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard title="Approval chains" subtitle="A request matches the first active rule whose condition holds; no match falls back to the reporting manager" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "label", header: "Rule" },
            { key: "triggerType", header: "Trigger", render: (r) => <Badge tone="neutral">{cap(r.triggerType)}</Badge> },
            { key: "condition", header: "Condition", render: (r) => r.condition?.field ? `${r.condition.field} ${r.condition.op} ${r.condition.value}` : "Always" },
            {
              key: "chain", header: "Chain",
              render: (r) => (
                <Row gap={6} wrap>
                  {(r.chain || []).map((c, i) => (
                    <React.Fragment key={`${c}${i}`}>
                      <Badge tone="neutral">{i + 1}. {cap(c)}</Badge>
                      {i < r.chain.length - 1 && <ArrowRight size={11} style={{ color: t.c.textTertiary }} />}
                    </React.Fragment>
                  ))}
                </Row>
              ),
            },
            {
              key: "active", header: "Active", align: "right",
              render: (r) => isHr
                ? <Toggle checked={r.active !== false} onChange={async (v) => { await patch(`/approval-rules/${r._id}`, { active: v }); refresh(); }} />
                : <StatusBadge value={r.active !== false ? "Active" : "Off"} />,
            },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No rules" description="Initialize defaults on the Organization tab." />}
        />
      </SectionCard>

      {isHr && (
        <SectionCard title="Add rule" padding={16}>
          <Row gap={12} wrap align="flex-end">
            <Field label="Trigger">
              <Select value={form.triggerType} onChange={(e) => setForm((f) => ({ ...f, triggerType: e.target.value }))}>
                {TRIGGERS.map((x) => <option key={x} value={x}>{cap(x)}</option>)}
              </Select>
            </Field>
            <Field label="Label"><Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Leave · more than 10 days" /></Field>
            <Field label="Chain">
              <Select value={form.chain} onChange={(e) => setForm((f) => ({ ...f, chain: e.target.value }))}>
                {CHAIN_ROLES.map((x) => <option key={x} value={x}>{cap(x)}</option>)}
                <option value="both">Manager, then HR</option>
              </Select>
            </Field>
            <Field label="Condition field" hint="optional"><Input value={form.condField} onChange={(e) => setForm((f) => ({ ...f, condField: e.target.value }))} placeholder="days" /></Field>
            <Field label="greater than"><Input type="number" value={form.condValue} onChange={(e) => setForm((f) => ({ ...f, condValue: e.target.value }))} placeholder="10" /></Field>
            <Button variant="primary" leftIcon={<Plus size={13} />} onClick={add}>Add</Button>
          </Row>
        </SectionCard>
      )}
    </Stack>
  );
}

// ── Onboarding templates ──
function OnboardingTab({ t }) {
  const { data, loading } = useFetch("/onboarding-templates");

  return (
    <Stack gap={t.s[4]}>
      {(data || []).map((tpl) => (
        <SectionCard key={tpl._id} title={tpl.name} subtitle={`${tpl.tasks?.length || 0} tasks · owners resolve at start (manager → reporting manager, employee → the joiner)`} padding={0}>
          <DataTable
            columns={[
              { key: "task", header: "Task" },
              { key: "ownerRole", header: "Owner", render: (r) => <Badge tone="neutral">{cap(r.ownerRole)}</Badge> },
              { key: "whenLabel", header: "When", render: (r) => r.whenLabel || "—" },
              { key: "dueOffsetDays", header: "Due offset", align: "right", render: (r) => r.dueOffsetDays === null ? "—" : `${r.dueOffsetDays >= 0 ? "+" : ""}${r.dueOffsetDays}d` },
            ]}
            rows={tpl.tasks || []}
            rowKey={(r) => r.task}
          />
        </SectionCard>
      ))}
      {!loading && (data || []).length === 0 && (
        <EmptyState title="No templates" description="Initialize defaults on the Organization tab to create the standard joiner checklist." />
      )}
      <Hint>Start an onboarding from Lifecycle ▸ Onboarding — the template becomes owned, dated tasks for HR, IT, the manager and the joiner.</Hint>
    </Stack>
  );
}

// ── Profile fields ──
function FieldsTab({ t }) {
  const { isHr, post, del } = useHrms();
  const { data, loading, refresh } = useFetch("/profile-fields");
  const [form, setForm] = React.useState({ group: "personal", label: "", type: "text" });

  const add = async () => {
    if (!form.label.trim()) return;
    await post("/profile-fields", {
      ...form,
      fieldKey: form.label.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
      source: "custom",
    });
    setForm({ group: "personal", label: "", type: "text" });
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard title="Profile fields" subtitle="What the employee profile captures; statutory fields are visible to HR & payroll only" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "label", header: "Field" },
            { key: "group", header: "Group", render: (r) => <Badge tone="neutral">{cap(r.group)}</Badge> },
            { key: "type", header: "Type" },
            { key: "visibility", header: "Visibility", render: (r) => r.visibility === "hr_payroll" ? <Badge tone="warning">HR & payroll</Badge> : "Everyone" },
            { key: "source", header: "Source", render: (r) => r.source === "custom" ? <Badge tone="info">Custom</Badge> : "Standard" },
            {
              key: "act", header: "", align: "right",
              render: (r) => isHr && r.source === "custom"
                ? <Button size="sm" variant="ghost" onClick={async () => { await del(`/profile-fields/${r._id}`); refresh(); }}>Remove</Button>
                : null,
            },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No fields" description="Initialize defaults on the Organization tab." />}
        />
      </SectionCard>

      {isHr && (
        <SectionCard title="Add custom field" padding={16}>
          <Row gap={12} wrap align="flex-end">
            <Field label="Group">
              <Select value={form.group} onChange={(e) => setForm((f) => ({ ...f, group: e.target.value }))}>
                {["personal", "work", "statutory", "emergency", "other"].map((g) => <option key={g} value={g}>{cap(g)}</option>)}
              </Select>
            </Field>
            <Field label="Label"><Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="T-shirt size" /></Field>
            <Field label="Type">
              <Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                {["text", "date", "number", "select"].map((ty) => <option key={ty} value={ty}>{cap(ty)}</option>)}
              </Select>
            </Field>
            <Button variant="primary" leftIcon={<Plus size={13} />} onClick={add}>Add</Button>
          </Row>
        </SectionCard>
      )}
    </Stack>
  );
}

const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).replace(/_/g, " ") : "—");
